import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getLeagueForEntry } from "core-integration/src/store/entries/reducers";
import { fetchEntrySummary } from "core-integration/src/store/entries/thunks";
import { getCode } from "core-integration/src/store/leagues/reducers";
import { fetchLeagueCode } from "core-integration/src/store/leagues/thunks";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { ILoggedInPlayer } from "core-integration/src/store/player/types";
import { Button } from "plos/src/components/buttons/Button";
import HorizontalDivider from "plos/src/components/HorizontalDivider";
import PageTitle from "plos/src/components/PageTitle";
import { SurfaceContainer } from "plos/src/components/SurfaceContainer";
import { rightSidebarLayout } from "plos/src/layouts";
import { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import useClipboard from "react-use-clipboard";
import { useTrackingContext } from "../../../contexts/TrackingContext";
import { leaguesMainContent } from "../../LeaguesAndCups/leaguesAndCups.css";
import HelmetHeadLeagues from "../HelmetHeadLeagues";
import {
  codeText,
  codeWrapper,
  emailTextStyles,
  gridDivider,
  inviteContentGrid,
  sectionWrapper,
} from "./invite.css";

const inviteMessage = (code: string, leagueName: string) => [
  "Hello,",
  `For the 2026/27 Fantasy Premier League why don't you join my mini-league "${leagueName}"?`,
  `Joining the league couldn't be easier. Simply use the link below and you'll be added automatically after you've entered the game.`,
  `${window.location.origin}/leagues/auto-join/${code}`,
  `League Code: ${code}`,
  "Looking forward to playing against you this season!",
];

const Invite = () => {
  const { t } = useTranslation("leaguesAndCups");
  const { logBranchEvent } = useTrackingContext();
  const { leagueId } = useParams();
  const dispatch = useDispatch<ThunkDispatch>();
  const leagueNumber = leagueId ? parseInt(leagueId, 10) : 0;

  const player = useSelector(
    (state: RootState) => getPlayerData(state) as ILoggedInPlayer,
  );

  const entryId = player.entry;
  const code = useSelector((state: RootState) =>
    leagueNumber ? getCode(state, leagueNumber) : null,
  );
  const league = useSelector((state: RootState) =>
    leagueNumber ? getLeagueForEntry(state, entryId, leagueNumber) : null,
  );

  const codeToCopy = code ? code : "";
  const leagueName = league ? league.name : "";
  const message = inviteMessage(codeToCopy, leagueName);
  const emailText = message ? message.join("\n\n") : "";
  const emailBody = encodeURIComponent(emailText);
  const joinUrl = `${window.location.origin}/leagues/auto-join/${code}`;
  const [isCopiedUrl, setCopiedUrl] = useClipboard(joinUrl, {
    successDuration: 500,
  });
  const [isCopiedEmail, setCopiedEmail] = useClipboard(emailText, {
    successDuration: 500,
  });

  const sendInvite = () => {
    logBranchEvent("INVITE");
  };

  const handleCopyUrl = () => {
    sendInvite();
    setCopiedUrl();
  };

  const handleCopyEmail = () => {
    sendInvite();
    setCopiedEmail();
  };

  const handleShareByEmail = () => {
    sendInvite();
    window.location.href = `mailto:?to=&subject=Play%20Fantasy%20Premier%20League&body=${emailBody}`;
  };

  useEffect(() => {
    if (leagueNumber) {
      dispatch(fetchLeagueCode(leagueNumber));
    }
    dispatch(fetchEntrySummary(entryId));
  }, [dispatch, leagueNumber, entryId]);

  if (!codeToCopy || !leagueNumber || !league) {
    return null;
  }

  return (
    <>
      <HelmetHeadLeagues />
      <div className={rightSidebarLayout}>
        <SurfaceContainer>
          <div className={leaguesMainContent}>
            <PageTitle
              title={t(
                "invitePlayersWithLeagueName",
                "Invite players to join {{leagueName}}",
                { leagueName },
              )}
            />
            <div className={inviteContentGrid}>
              <div className={sectionWrapper}>
                <div className={codeWrapper}>
                  <p>{t("codeToJoinLeague", "Code to join this league:")}</p>
                  <strong className={codeText}>
                    {isCopiedUrl ? t("copied", "Copied!") : codeToCopy}
                  </strong>
                </div>
                <div>
                  <Button appearance="outlined" onPress={handleCopyUrl}>
                    {t("copyAutoJoinLink", "Copy auto join link")}
                  </Button>
                </div>
              </div>
              <div className={gridDivider}>
                <HorizontalDivider />
              </div>
              <div className={sectionWrapper}>
                <p>
                  <Trans ns="leaguesAndCups" i18nKey="inviteEmailInstructions">
                    When you click the link below, the link will attempt to open
                    your default email program and start a new email. If you do
                    not have a default email program, like Outlook or Mail,
                    enabled on your computer, clicking the link may not result
                    in any action or you may be prompted to set up an email
                    program.
                  </Trans>
                </p>
                <div>
                  <Trans ns="leaguesAndCups" i18nKey="shareLeagueCodeButton">
                    <Button onPress={handleShareByEmail}>
                      Share league code by email
                    </Button>
                  </Trans>
                </div>
              </div>
              <div className={gridDivider}>
                <HorizontalDivider />
              </div>
              <div className={sectionWrapper}>
                <p>
                  <Trans
                    ns="leaguesAndCups"
                    i18nKey="copyEmailTextInstructions"
                  >
                    If you use a browser based webmail application, like Gmail
                    or Yahoo!, you can copy and paste the text below:
                  </Trans>
                </p>
                <div
                  className={emailTextStyles}
                  onDoubleClick={handleCopyEmail}
                >
                  {message.map((line) => (
                    <p key={line}>{line}</p>
                  ))}
                </div>
                <div>
                  <Button onPress={handleCopyEmail}>
                    {isCopiedEmail
                      ? t("copied", "Copied!")
                      : t("copyEmailText", "Copy email text")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </SurfaceContainer>
      </div>
    </>
  );
};

export default Invite;
